import React, { Component } from 'react';
import PropTypes from 'prop-types';

import uuid from 'uuid';
import Waypoint from 'react-waypoint';
import { HotKeys } from 'react-hotkeys';

import _findIndex from 'lodash/findIndex';
import _findLastIndex from 'lodash/findLastIndex';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';

import {
  dictionariesEntry,
  dictionariesColumn,
  paginationOptionsType,
} from 'types';

// FIXME: AddNewRow should be moved to Thead
import AddNewRow from '../AddNewRow';
import Thead from '../TableHead';
import Tbody from '../TableBody';

import './style.scss';

const HOTKEYS_MAP = {
  focusNextCol: ['tab'],
  focusPrevCol: ['shift+tab'],
  focusNextRow: ['down', 'enter'],
  focusPrevRow: ['up'],
};

export class DictionariesEntriesTable extends Component {
  static propTypes = {
    entries: PropTypes.arrayOf(dictionariesEntry),
    entriesPagination: paginationOptionsType,
    columns: PropTypes.arrayOf(dictionariesColumn),
    selection: PropTypes.arrayOf(PropTypes.string),
    toggleSelection: PropTypes.func,
    toggleAllSelection: PropTypes.func,
    onRemoveRow: PropTypes.func.isRequired,
    sortItems: PropTypes.func.isRequired,
    loadNextEntries: PropTypes.func,
    saveEntry: PropTypes.func.isRequired,
    onSaveNewEntry: PropTypes.func,
    onSaveNewColumn: PropTypes.func,
    onSaveColumnUpdate: PropTypes.func,
    onDeleteColumn: PropTypes.func,
  };

  state = {
    entries: this.props.entries,
    columns: this.props.columns,
    selection: this.props.selection,
    focus: {},
  };

  componentWillReceiveProps(nextProps) {
    if (
      !_isEqual(nextProps.entries, this.props.entries) ||
      !_isEqual(nextProps.columns, this.props.columns) ||
      !_isEqual(nextProps.selection, this.props.selection)
    ) {
      this.setState({
        entries: nextProps.entries,
        columns: nextProps.columns,
        selection: nextProps.selection,
      });
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      !_isEqual(nextProps.entries, this.props.entries) ||
      !_isEqual(nextProps.columns, this.props.columns) ||
      !_isEqual(nextProps.entriesPagination, this.props.entriesPagination) ||
      !_isEqual(nextState, this.state)
    );
  }

  handleFocus = (focus = {}) => {
    this.setState({ focus });
  };

  handleUpdateRow = (editedEntry, idToUpdate, value) =>
    this.setState(prevState => ({
      entries: prevState.entries.map(entry => {
        if (entry.id === editedEntry.id) {
          if (idToUpdate === 'token') {
            return {
              ...entry,
              token: value,
            };
          }

          return {
            ...entry,
            values: {
              ...entry.values,
              [idToUpdate]: value,
            },
          };
        }

        return entry;
      }),
    }));

  // TODO: make a new way to define redundant updates
  handleSave = entry => {
    const prevEntry = this.props.entries.find(i => i.id === entry.id);

    if (
      entry.token !== prevEntry.token ||
      !_isEqual(entry.values, prevEntry.values)
    ) {
      this.props.saveEntry(entry.id, entry);
    }
  };

  handleAddColumn = () => {
    const newColumn = {
      name: '',
      order: this.state.columns.length,
      renderKey: uuid(),
    };
    this.setState(prevState => ({
      focus: {},
      columns: [...prevState.columns, newColumn],
    }));
  };

  handleUpdateCol = (editedColumn, value) => {
    this.setState(({ columns }) => ({
      columns: columns.map(col => {
        if (!col.id || editedColumn.id === col.id) {
          return {
            ...col,
            name: value,
          };
        }

        return col;
      }),
    }));
  };

  handleDeleteColumn = ({ id, order }) => {
    if (id) {
      this.props.onDeleteColumn(id);
    } else {
      this.setState(prevState => ({
        columns: prevState.columns.filter(column => column.order !== order),
      }));
    }
  };

  handleSelect = id => {
    const { selection } = this.state;
    const isSelected = selection.indexOf(id) >= 0;
    const newSelection = isSelected
      ? selection.filter(currentID => currentID !== id)
      : [...selection, id];

    this.setState(
      {
        selection: newSelection,
      },
      () => this.props.toggleSelection(id),
    );
  };

  handleToggleAllSelection = () => {
    const { selection, entries } = this.state;
    this.setState(
      {
        // if all are selected
        selection:
          selection.length === entries.length
            ? []
            : entries.map(entry => entry.id),
      },
      this.props.toggleAllSelection,
    );
  };

  handleLoadNextEntries = () => {
    this.props.loadNextEntries();
  };

  identifyAllSelection = (selection, entries) =>
    selection.length > 0 && selection.length === entries.length;

  identifyPartialSelection = (selection, entries) =>
    selection.length > 0 && selection.length < entries.length;

  createFocusMoveHandler = (hor = 0, ver = 0) => e => {
    const { focus } = this.state;

    e.preventDefault();

    if (!_isEmpty(focus)) {
      const { col, row } = focus;
      const { columns, entries } = this.props;
      const findBy = [columns, { archived: false }];

      let nextRow = row + hor;
      let nextCol = col;

      if (ver !== 0) {
        if (ver > 0) {
          nextCol = _findIndex(...findBy, col + ver);

          if (nextCol === -1) {
            nextCol = -1;
            nextRow += 1;
          }
        } else {
          nextCol = -1;

          if (col > 0) {
            nextCol = _findLastIndex(...findBy, col + ver);
          }

          if (col < 0) {
            nextCol = _findLastIndex(...findBy);
            nextRow -= 1;
          }
        }
      }

      this.handleFocus({
        // Sets focus to beginning (skipping input row) IF the last ROW or COL
        row: nextRow < entries.length ? Math.max(nextRow, -1) : 0,
        col: nextCol < columns.length ? Math.max(nextCol, -1) : 0,
      });
    }
  };

  handleKeys = {
    focusNextCol: this.createFocusMoveHandler(0, 1),
    focusNextRow: this.createFocusMoveHandler(1, 0),
    focusPrevCol: this.createFocusMoveHandler(0, -1),
    focusPrevRow: this.createFocusMoveHandler(-1, 0),
  };

  renderThead = () => {
    const { onSaveNewEntry, entriesPagination } = this.props;
    const { entries, columns, selection, focus } = this.state;

    /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
    /* eslint-disable jsx-a11y/no-static-element-interactions */
    /* eslint-disable jsx-a11y/click-events-have-key-events */

    return (
      <thead>
        <Thead
          columns={columns}
          entriesPagination={entriesPagination}
          focus={focus}
          isAllSelected={this.identifyAllSelection(selection, entries)}
          isPartialSelected={this.identifyPartialSelection(selection, entries)}
          sortItems={this.props.sortItems}
          onAddColumn={this.handleAddColumn}
          onDeleteColumn={this.handleDeleteColumn}
          onFocus={this.handleFocus}
          onSaveColumnUpdate={this.props.onSaveColumnUpdate}
          onSaveNewColumn={this.props.onSaveNewColumn}
          onSaveNewEntry={this.props.onSaveNewEntry}
          onToggleAllSelection={this.handleToggleAllSelection}
          onUpdateCol={this.handleUpdateCol}
        />

        <AddNewRow
          columns={columns}
          focus={focus}
          onFocus={this.handleFocus}
          onSaveNewEntry={onSaveNewEntry}
        />
      </thead>
    );
  };

  render() {
    const { onRemoveRow } = this.props;
    const { entries, columns, selection, focus } = this.state;

    /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
    /* eslint-disable jsx-a11y/click-events-have-key-events */
    /* eslint-disable jsx-a11y/no-static-element-interactions */

    return (
      <div styleName="root">
        <HotKeys handlers={this.handleKeys} keyMap={HOTKEYS_MAP}>
          <table>
            {entries.length > 0 && (
              <Tbody
                columns={columns}
                entries={entries}
                focus={focus}
                selection={selection}
                onFocus={this.handleFocus}
                onRemoveRow={onRemoveRow}
                onSave={this.handleSave}
                onSelect={this.handleSelect}
                onUpdateRow={this.handleUpdateRow}
              />
            )}

            {/* Do NOT move it up, that's the fix for checkboxes z-index issue */}
            {this.renderThead()}
          </table>

          <Waypoint onEnter={this.handleLoadNextEntries} />
        </HotKeys>
      </div>
    );
  }
}

export default DictionariesEntriesTable;
